import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, Loader2, UserRound } from "lucide-react";
import { format, parseISO } from "date-fns";


/**
 * Pro Reputation Tooltip — click a customer chip on a job card to see their
 * last 3 anonymous ratings before accepting the booking.
 *
 * Positioned as a lightweight dropdown popover anchored to the clicked chip.
 */
export default function CustomerReviewPopover({ customerId, customerName, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!customerId) return;
        api.get(`/providers/me/customer-reviews/${customerId}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ reviews: [], avg_rating: 0, rating_count: 0 }))
            .finally(() => setLoading(false));
    }, [customerId]);

    return (
        <div
            data-testid="customer-review-popover"
            className="fixed inset-0 z-[90] bg-black/60 grid place-items-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-[#FFD700]/40 bg-background p-6"
            >
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 rounded-full bg-black/40 border border-white/10 grid place-items-center">
                        <UserRound size={20} className="text-[#FFD700]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.26em] text-amber-300/90">Customer reputation</div>
                        <h3 className="font-serif text-xl text-foreground truncate">{customerName}</h3>
                        {data && data.rating_count > 0 && (
                            <div className="flex items-center gap-1.5 mt-1 text-sm">
                                <Star size={14} fill="#FFD700" className="text-[#FFD700]" />
                                <span className="text-foreground font-mono">{data.avg_rating.toFixed(1)}</span>
                                <span className="text-xs text-muted-foreground">
                                    ({data.rating_count} rating{data.rating_count === 1 ? "" : "s"})
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-10 text-muted-foreground">
                        <Loader2 size={18} className="animate-spin" />
                    </div>
                )}

                {!loading && data && data.reviews.length === 0 && (
                    <div
                        data-testid="customer-review-empty"
                        className="text-center py-8 border border-dashed border-white/10 rounded-md"
                    >
                        <div className="text-sm text-foreground mb-1">No past reviews</div>
                        <div className="text-xs text-muted-foreground">First-time or unrated customer on SMART.</div>
                    </div>
                )}

                {!loading && data && data.reviews.length > 0 && (
                    <div className="space-y-3" data-testid="customer-review-list">
                        <div className="text-[10px] uppercase tracking-[0.26em] text-muted-foreground mb-2">
                            Last {data.reviews.length} rating{data.reviews.length === 1 ? "" : "s"} from pros
                        </div>
                        {data.reviews.map((r, i) => (
                            <div
                                key={`cust-review-${r.at || i}-${r.service_category || i}`}
                                data-testid={`customer-review-${i}`}
                                className="bg-black/30 border border-white/10 rounded-md p-3"
                            >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star
                                                key={n}
                                                size={13}
                                                fill={n <= r.rating ? "#FFD700" : "transparent"}
                                                className={n <= r.rating ? "text-[#FFD700]" : "text-white/15"}
                                            />
                                        ))}
                                    </div>
                                    <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                                        {r.service_category || "—"}
                                        {r.at && ` · ${format(parseISO(r.at), "MMM yyyy")}`}
                                    </div>
                                </div>
                                {r.comment && (
                                    <p className="text-sm text-foreground mt-1 leading-relaxed">{r.comment}</p>
                                )}
                            </div>
                        ))}
                        <div className="text-[10px] text-muted-foreground pt-2 text-center">
                            Pro identities hidden — SMART Royal Protection.
                        </div>
                    </div>
                )}

                <button
                    data-testid="customer-review-close"
                    onClick={onClose}
                    className="w-full mt-5 px-4 py-2.5 rounded-full border border-white/10 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:bg-white/5 transition"
                >
                    Close
                </button>
            </div>
        </div>
    );
}
